import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslations } from "@/hooks/useTranslations";
import styles from "./RightNav.module.scss";

interface ISectionItem {
  id: string;
  title: string;
}

interface IRightNavProps {
  sections: ISectionItem[];
  title?: string;
  ariaLabel?: string;
}

export function RightNav({ sections, title, ariaLabel }: IRightNavProps): ReactNode {
  const [activeSection, setActiveSection] = useState<string>("");
  const { t } = useTranslations();
  const clickLockRef = useRef<string | null>(null);
  const clickLockTimeoutRef = useRef<number | null>(null);
  const activeIndexRef = useRef<number>(-1);
  const lastScrollTopRef = useRef<number>(0);

  useEffect(() => {
    const handleScroll = (): void => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = window.innerHeight || document.documentElement.clientHeight;
      const isAtTop = scrollTop <= 1;
      const topOffset = 80;
      const remainingScroll = scrollHeight - scrollTop - clientHeight;
      const isAtBottom = scrollHeight > clientHeight && remainingScroll <= 1;
      const isNearBottom = scrollHeight > clientHeight && remainingScroll <= clientHeight;
      const bottomProgress = isNearBottom ? Math.max(0, Math.min(1, (clientHeight - remainingScroll) / clientHeight)) : 0;
      const activationOffset = topOffset + bottomProgress * (clientHeight - topOffset);
      const wasScrollingDown = scrollTop > lastScrollTopRef.current;
      lastScrollTopRef.current = scrollTop;

      if (isAtTop && sections.length > 0) {
        activeIndexRef.current = 0;
        setActiveSection((previous) => (previous === sections[0].id ? previous : sections[0].id));
        return;
      }

      const clickLockSectionId = clickLockRef.current;
      if (clickLockSectionId) {
        const lockedElement = document.getElementById(clickLockSectionId);
        if (lockedElement) {
          const lockedRect = lockedElement.getBoundingClientRect();
          const lockedVisible = lockedRect.top < clientHeight && lockedRect.bottom > 0;
          if (lockedVisible) {
            const lockedIndex = sections.findIndex((section) => section.id === clickLockSectionId);
            if (lockedIndex >= 0) {
              activeIndexRef.current = lockedIndex;
            }
            setActiveSection(clickLockSectionId);
            return;
          }
        }
      }

      // Determine the active section using a standard TOC rule:
      // - Pick the last heading whose top has reached the offset.
      // - If none have reached it yet, pick the first heading below the offset.
      //
      // IMPORTANT: we do not require the heading to be "visible"; once a heading scrolls
      // past the top it can be out of the viewport (especially when headings are tightly
      // stacked), but it should remain active until the next heading reaches the offset.
      let currentSectionIndex = -1;
      let firstBelowOffsetIndex = -1;

      const sectionElements = sections.map((section) => document.getElementById(section.id)).filter(Boolean);

      for (let sectionIndex = 0; sectionIndex < sectionElements.length; sectionIndex += 1) {
        const element = sectionElements[sectionIndex];
        const rect = element?.getBoundingClientRect();
        const distanceFromOffset = rect ? rect.top - activationOffset : Infinity;

        // Only consider the heading "active" once it has crossed the offset.
        // Using a positive tolerance here causes closely spaced headings (especially near the bottom)
        // to skip over the previous section.
        if (distanceFromOffset <= 0) {
          currentSectionIndex = sectionIndex;
        } else if (firstBelowOffsetIndex === -1) {
          firstBelowOffsetIndex = sectionIndex;
        }
      }

      if (currentSectionIndex === -1 && firstBelowOffsetIndex !== -1) {
        currentSectionIndex = firstBelowOffsetIndex;
      }

      // At the true bottom of the page, the final heading should be active.
      if (isAtBottom && sections.length > 0) {
        currentSectionIndex = sections.length - 1;
      }

      // While scrolling down, never skip over intermediate sections.
      if (!isAtBottom && wasScrollingDown && currentSectionIndex !== -1 && activeIndexRef.current >= 0) {
        const maxNextIndex = activeIndexRef.current + 1;
        if (currentSectionIndex > maxNextIndex) {
          currentSectionIndex = maxNextIndex;
        }
      }
      
      if (currentSectionIndex !== -1) {
        const nextSectionId = sections[currentSectionIndex].id;
        activeIndexRef.current = currentSectionIndex;
        setActiveSection((previous) => (previous === nextSectionId ? previous : nextSectionId));
      }
    };

    // Initial check
    handleScroll();

    // Listen to scroll events with throttling
    let ticking = false;
    const onScroll = (): void => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll);

    return (): void => {
      window.removeEventListener("scroll", onScroll);
      if (clickLockTimeoutRef.current !== null) {
        window.clearTimeout(clickLockTimeoutRef.current);
        clickLockTimeoutRef.current = null;
        clickLockRef.current = null;
      }
    };
  }, [sections]);

  if (sections.length === 0) {
    return null;
  }

  const resolvedTitle = title ?? t("navigation.inThisArticle");
  const resolvedAriaLabel = ariaLabel ?? resolvedTitle;

  function handleSectionClick(sectionId: string): void {
    // Set active immediately so the clicked section highlights even
    // when the page doesn't scroll (e.g., the target is already in view).
    setActiveSection(sectionId);
    const clickedIndex = sections.findIndex((section) => section.id === sectionId);
    if (clickedIndex >= 0) {
      activeIndexRef.current = clickedIndex;
    }
    clickLockRef.current = sectionId;
    if (clickLockTimeoutRef.current !== null) {
      window.clearTimeout(clickLockTimeoutRef.current);
    }
    clickLockTimeoutRef.current = window.setTimeout(() => {
      clickLockRef.current = null;
      clickLockTimeoutRef.current = null;
    }, 1000);

    window.history.pushState(null, "", `#${sectionId}`);
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <nav className={styles.rightNav} aria-label={resolvedAriaLabel}>
      <h2 className={styles.title}>{resolvedTitle}</h2>
      <ul className={styles.sectionList}>
        {sections.map((section) => (
          <li key={section.id} className={styles.sectionItem}>
            <button
              type="button"
              className={`${styles.sectionLink} ${activeSection === section.id ? styles.active : ""}`}
              onClick={(): void => handleSectionClick(section.id)}
              aria-current={activeSection === section.id ? "location" : undefined}
            >
              {section.title}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
